/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/register.scss';

import React, {FocusEventHandler, MouseEventHandler, useMemo, useRef, useState} from 'react';

import {ElfsightWidget} from 'react-elfsight-widget';

import {RegisterErrors, RegisterStep} from '../../../util/interfaces';

import User from '../../../models/userModel';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    Link,
    Loader,
    preventDefault
} from '@dvrd/dvr-controls';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import {AppLanguage, normalizeLanguage} from '../../../i18n/config';

import {getCurrentLanguage} from '../../../i18n/languageService';

import PasswordStrength from '../../password/passwordStrength';

import {BolmateButton, BolmateInput, BolmatePasswordInput} from '../../controls';

import {CredentialResponse, GoogleLogin} from '@react-oauth/google';

import Image from '../../misc/image';

import {LOGIN_ROUTE} from '../../../util/constants';

import {useTranslation} from 'react-i18next';

import {useBreakpoint} from '../../../util/useBreakpoint';

interface Props {
    onChangeUser: ChangeKeyFunction;
    onChangePassword: ChangeFunction;
    onChangeTerms: ChangeFunction;
    onChangeCompanyName: ChangeFunction;
    onSubmit: MouseEventHandler;
    onClickTrustPilot: MouseEventHandler;
    onResetError: (key: keyof RegisterErrors) => FocusEventHandler;
    onGoogleSuccess: (response: CredentialResponse) => void;
    onGoogleError: VoidFunction;
    onClickLogin: MouseEventHandler;
    onClickGoogle: MouseEventHandler;
    onChangeStep: (step: RegisterStep) => MouseEventHandler;
    user: User;
    password: string;
    loading: boolean;
    errors: RegisterErrors;
    termsAccepted: boolean;
    companyName: string;
    step: RegisterStep;
    usersEstimate: number;
    isInvited: boolean;
}

export default function Register(props: Props) {
    const {
        user,
        password,
        errors,
        onResetError,
        onChangePassword,
        onChangeCompanyName,
        onChangeTerms,
        onChangeUser,
        step,
        loading,
        onClickGoogle,
        onClickLogin,
        onGoogleSuccess,
        onGoogleError,
        termsAccepted,
        companyName,
        onSubmit,
        onClickTrustPilot,
        usersEstimate,
        isInvited,
        onChangeStep
    } = props;
    const {t, i18n} = useTranslation();
    const header = useRef<HTMLDivElement>(null);
    const [language, setUiLanguage] = useState<AppLanguage>(getCurrentLanguage());
    const bp = useBreakpoint();
    // The Google button renders in an iframe with a fixed pixel width, so it
    // cannot shrink via CSS — size it per breakpoint tier instead.
    const googleButtonWidth = bp.isTiny ? '260px' : bp.isMobile ? '320px' : '400px';
    const languageItems = useMemo(
        () => [
            {
                value: 'nl',
                label: (
                    <span>
                        <span className='fi fi-nl' style={{margin: '0 0.375rem'}} />{' '}
                        {t('settings.language.nl', 'Nederlands')}
                    </span>
                )
            },
            {
                value: 'en',
                label: (
                    <span>
                        <span className='fi fi-gb' style={{margin: '0 0.375rem'}} />{' '}
                        {t('settings.language.en', 'Engels')}
                    </span>
                )
            }
        ],
        [t]
    );

    function onChangeLanguage(nextLanguage: string | null) {
        if (!nextLanguage) return;
        const normalized = normalizeLanguage(nextLanguage);
        if (normalized !== 'nl' && normalized !== 'en') return;
        setUiLanguage(normalized);
        void i18n.changeLanguage(normalized);
    }

    function onClickTermsLabel() {
        onChangeTerms(!termsAccepted);
    }

    function renderGoogle() {
        return (
            <div onClick={onClickGoogle} className='google-btn-wrapper'>
                <GoogleLogin
                    onSuccess={onGoogleSuccess}
                    onError={onGoogleError}
                    text='signup_with'
                    context='signup'
                    width={googleButtonWidth}
                />
            </div>
        );
    }

    function renderForm() {
        return (
            <form onSubmit={preventDefault} className='form'>
                <BolmateInput
                    onChange={onChangeCompanyName}
                    value={companyName}
                    label={t('auth.register.companyNameLabel', 'Bedrijfsnaam *')}
                    error={errors.companyName}
                    onFocus={onResetError('companyName')}
                    inputProps={{placeholder: t('auth.register.companyNamePlaceholder', 'Bedrijfsnaam')}}
                    className='register-field'
                    disabled={isInvited}
                    autoFocus
                />
                <BolmateInput
                    value={user.firstname}
                    onChange={onChangeUser('firstname')}
                    error={errors.firstname}
                    onFocus={onResetError('firstname')}
                    label={t('auth.register.firstnameLabel', 'Voornaam *')}
                    inputProps={{placeholder: t('auth.register.firstnamePlaceholder', 'Voornaam')}}
                    className='register-field'
                />
                <BolmateInput
                    value={user.lastname}
                    onChange={onChangeUser('lastname')}
                    error={errors.lastname}
                    onFocus={onResetError('lastname')}
                    label={t('auth.register.lastnameLabel', 'Achternaam *')}
                    inputProps={{placeholder: t('auth.register.lastnamePlaceholder', 'Achternaam')}}
                    className='register-field'
                />
                <BolmateInput
                    value={user.email}
                    onChange={onChangeUser('email')}
                    label={t('auth.register.emailLabel', 'E-mailadres *')}
                    error={errors.email}
                    onFocus={onResetError('email')}
                    className='register-field'
                    inputProps={{
                        type: 'email',
                        autoComplete: 'email',
                        placeholder: t('auth.register.emailPlaceholder', 'E-mailadres')
                    }}
                />
                <BolmatePasswordInput
                    value={password}
                    onChange={onChangePassword}
                    label={t('auth.register.passwordLabel', 'Wachtwoord *')}
                    inputProps={{
                        autoComplete: 'new-password',
                        placeholder: t('auth.register.passwordPlaceholder', 'Wachtwoord')
                    }}
                    error={errors.password}
                    onFocus={onResetError('password')}
                    className='register-field'
                />
                <PasswordStrength value={password} />
                <div className='disclaimer-container'>
                    <Checkbox checked={termsAccepted} onCheck={onChangeTerms} />
                    <label className='disclaimer' onClick={onClickTermsLabel}>
                        {t('auth.register.termsAgreePrefix', 'Ik ga akkoord met de')}{' '}
                        <Link route='https://bolmate.nl/utility/algemene-voorwaarden/' target='_blank'>
                            {t('auth.register.termsConditions', 'Algemene voorwaarden')}
                        </Link>{' '}
                        ,{' '}
                        <Link route='https://bolmate.nl/privacy-statement/' target='_blank'>
                            {t('auth.register.privacyStatement', 'Privacy statement')}
                        </Link>{' '}
                        {t('auth.register.and', 'en')}{' '}
                        <Link route='https://bolmate.nl/verwerkingsovereenkomst/' target='_blank'>
                            {t('auth.register.processingAgreement', 'Verwerkersovereenkomst')}
                        </Link>
                        .
                    </label>
                </div>
            </form>
        );
    }

    function renderRegistrationChoice() {
        return (
            <>
                <div className='header-container'>
                    <div className='logo-header' ref={header}>
                        <Image name='logo' className='logo' />
                    </div>
                    <h1 className='header-title'>{t('auth.register.freeTrialTitle', 'Probeer 14 dagen gratis!')}</h1>

                    <label className='sub-title'>
                        {t(
                            'auth.register.freeTrialSubtitle',
                            'Stopt automatisch. Verlenging mogelijk. Geen betaalmethode nodig.'
                        )}
                    </label>
                </div>
                <div className='login-btns-container'>
                    {renderGoogle()}
                    <span className='or-label'>{t('auth.register.or', '- of -')}</span>
                    <div className='btns-container'>
                        <BolmateButton
                            onClick={onChangeStep(RegisterStep.FORM)}
                            label={t('auth.register.signupWithEmail', 'Aanmelden met e-mailadres')}
                            className='email-btn'
                            disabled={loading}
                            ornaments={{element: <AwesomeIcon name='envelope' prefix='far' />}}
                            ornamentClassName='email-icon'
                        />
                    </div>
                </div>
                <label className='bottom-text'>
                    {t('auth.register.haveAccount', 'Heb je al een Bolmate account?')}{' '}
                    <Link route={LOGIN_ROUTE} onClick={onClickLogin} underline>
                        {t('auth.register.login', 'Inloggen')}
                    </Link>
                </label>
            </>
        );
    }

    function renderRegistrationForm() {
        return (
            <>
                {!isInvited && (
                    <div className='back-button' onClick={onChangeStep(RegisterStep.METHOD_CHOICE)}>
                        <AwesomeIcon name='arrow-left' />
                    </div>
                )}
                <div className='header-container'>
                    <div className='logo-header' ref={header}>
                        <Image name='logo' alt='Bolmate' className='logo' />
                    </div>
                </div>
                <div className='content'>
                    {renderForm()}
                    <div className='submit-button'>
                        <BolmateButton
                            onClick={onSubmit}
                            label={t('auth.register.createAccount', 'Maak een account aan')}
                            className='submit-btn'
                            disabled={loading || !termsAccepted}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className='register'>
            <div style={{position: 'absolute', top: '1rem', right: '1rem', zIndex: 10}}>
                <PortalSelectController value={language} onChange={onChangeLanguage} items={languageItems} />
            </div>
            <div className='register-first'>
                <Image name='registrationBG' alt='Bolmate' className='background' />
                <div className='content-left'>
                    <Loader active={loading} />
                    {step === 0 ? renderRegistrationChoice() : renderRegistrationForm()}
                </div>
                <div className='content-right'>
                    <h1 className='header-title'>
                        {t('auth.register.companiesUsingBolmate', 'Meer dan {{total}}+ bedrijven gebruiken Bolmate!', {
                            total: usersEstimate
                        })}
                    </h1>
                    <Image name='logos-companies' alt='Bolmate' className='logo' />
                    <div className='rating-container'>
                        <div className='google-container'>
                            <ElfsightWidget widgetId='1338afd7-4616-4fa9-8aa3-80cf163be59e' lazy modern />
                        </div>
                        <div className='trustpilot-container'>
                            <Image name='trustpilot' alt='Trustpilot' className='img' onClick={onClickTrustPilot} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
